import type { ChangeEvent, FC } from 'react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Form } from 'react-final-form'
import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { EColor } from '@frontend/pole-ui/lib/common/constants'
import { CommonLink } from '@frontend/pole-ui/lib/components/CommonLink'
import type { FormApi } from 'final-form'
import compact from 'lodash/compact'
import debounce from 'lodash/debounce'
import { Button, ErrorMessage, InjectHtml, Loader } from '@/components/ui'
import { Checkbox } from '@/components/ui/fields'
import { isAuthenticated as isUserAuthenticated } from '@/logic/auth/reducer'
import { useData } from '@/logic/data'
import { loadData } from '@/logic/data/actions'
import { getLegalDocumentUrl } from '@/logic/legalDocuments/helpers'
import { getProfile } from '@/logic/profile/reducer'
import { getRequestState } from '@/logic/requestStatus/reducer'
import { injectMessages } from '@/logic/translation/utils'
import type { RootState } from '@/redux/interfaces'
import type { IUserOrganizationData } from '@/types'
import type { TLegalDocuments } from '@/types/legalDocuments'
import { ELegalDocumentType } from '@/types/legalDocuments'
import { addNewEpicsLazily } from '@/utils/addNewEpicsLazily'
import { useDeviceType } from '@/utils/hooks/useDeviceType'
import { sendFeedback } from './actions'
import { FeedbackField } from './components'
import { fields, requestStatusName } from './constants'
import asyncEpics from './epics'
import type { IFeedbackFormValues } from './interfaces'
import messages from './messages.json'
import './styles.scss'

addNewEpicsLazily([asyncEpics])
injectMessages(messages)

const mapState = (state: RootState) => ({
  profile: getProfile()(state),
  requestState: getRequestState(requestStatusName)(state),
  isAuthenticated: isUserAuthenticated()(state),
})

const Feedback: FC = () => {
  const dispatch = useDispatch()
  const { profile, requestState, isAuthenticated } = useSelector(mapState)
  const { isMobile } = useDeviceType()

  const { isLoading: isFetching, isLoaded: isOrganizationDataLoaded } = useData<IUserOrganizationData>('userOrganization', {
    shouldLoadForcibly: true,
  })
  const [message, setMessage] = useState<string>('')
  const [initialMessage, setInitialMessage] = useState<string>('')
  const { data: documents = [] } = useData<TLegalDocuments>('legalDocuments')
  const saveMessage = useMemo(() => debounce(({ target }: ChangeEvent<HTMLInputElement>) => setMessage(target.value), 500), [])
  const initialMessageRef = useRef<string>('')
  const { isLoading, isFailure, isLoaded } = requestState
  const { firstName, middleName, lastName, email = '', phone = '' } = profile || {}

  const initialValues: Partial<IFeedbackFormValues> = {
    fullName: compact([firstName, middleName, lastName]).join(' '),
    phone,
    email,
    message: initialMessage,
  }
  initialMessageRef.current = message
  const formRef = useRef<FormApi<IFeedbackFormValues>>()

  useEffect(() => {
    if (!isAuthenticated) return

    /*необходимо переустановить message в initialValues после авторизации пользователя,
    иначе набранное пользователем сообщение сбрасывается*/
    if (isOrganizationDataLoaded) setInitialMessage(initialMessageRef.current)
    else dispatch(loadData({ name: 'userOrganization', shouldLoadForcibly: true }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isOrganizationDataLoaded])

  useEffect(() => {
    if (isLoaded) formRef.current?.change('message', '')
  }, [isLoaded])

  return (
    <div className="feedback-page-container">
      <Loader isVisible={isFetching} />
      <div className="page-wrapper feedback-page-form-content page-wrapper_no-mobile">
        <div className="feedback-page-form-caption">
          <h2 className="space-holder24">
            <InjectHtml textId={`feedbackPage.feedbackForm.title${isMobile ? '.mobile' : ''}`} TagName="span" />
          </h2>
          <InjectHtml textId="feedbackPage.feedbackForm.supports.description" TagName="p" />
        </div>
        <Form<IFeedbackFormValues>
          initialValues={initialValues}
          onSubmit={values => dispatch(sendFeedback({ ...values, phone: values.phone || '', email: values.email || '' }))}
        >
          {({ handleSubmit, values, hasValidationErrors, errors, form }) => {
            formRef.current = form

            return (
              <form
                onSubmit={handleSubmit}
                className="feedback-page-form space-holder-top8 space-holder-top40-only-md space-holder-top32-sm"
              >
                {fields.map(fieldProps => (
                  <FeedbackField
                    {...fieldProps}
                    key={fieldProps.fieldName}
                    form={form}
                    initialValues={initialValues}
                    values={values}
                    errors={errors}
                    saveMessage={saveMessage}
                    isAuthenticated={isAuthenticated}
                  />
                ))}

                {!isAuthenticated && (
                  <Checkbox
                    name="agreement"
                    id="agreement"
                    className="feedback-page-form__checkbox space-holder-top8"
                    activeControlStyle={EColor.WHITE}
                    borderColorStyle={EColor.LIGHT_GRAY}
                    isRequired
                    sizeMobile="large"
                    markColor="green"
                  >
                    <div>
                      <FormattedMessage
                        id="feedbackPage.acceptingForm"
                        values={{
                          registrationRulesLink: (
                            <CommonLink
                              shouldOpenInNewWindow
                              url={getLegalDocumentUrl(documents, ELegalDocumentType.registrationRules)}
                              className="underline underline_white color_white link-no-hover"
                            >
                              {<FormattedMessage id="legalDocumentsPage.userRegistrationRules.text" />}
                            </CommonLink>
                          ),
                          registrationPolicyLink: (
                            <CommonLink
                              shouldOpenInNewWindow
                              url={getLegalDocumentUrl(documents, ELegalDocumentType.registrationPolicy)}
                              className="underline underline_white color_white link-no-hover"
                            >
                              {<FormattedMessage id="legalDocumentsPage.userRegistrationPolicy.text" />}
                            </CommonLink>
                          ),
                        }}
                      />
                    </div>
                  </Checkbox>
                )}

                <p className="space-holder32 space-holder-top16 text_super-small feedback-page-form__notice">
                  <FormattedMessage id="feedbackPage.feedbackForm.description" />
                </p>

                <ErrorMessage
                  isVisible={isFailure}
                  textId="feedbackPage.callback.error"
                  className="feedback-page-form__error space-holder24"
                />
                <Button
                  type="submit"
                  modifiers={['white-black', 'full-width-mobile']}
                  textId="feedbackPage.feedbackForm.button"
                  isDisabled={hasValidationErrors}
                  loaderProps={{ isVisible: isLoading || isFetching }}
                />
              </form>
            )
          }}
        </Form>
      </div>
    </div>
  )
}

export default Feedback
